import { ApplicationRef, Injectable, inject } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { BehaviorSubject, Observable, concat, interval } from 'rxjs';
import { first } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PwaUpdateService {
  private swUpdate = inject(SwUpdate);
  private appRef = inject(ApplicationRef);
  private updateAvailable$ = new BehaviorSubject<boolean>(false);

  constructor() {
    this.initServiceWorkerUpdates();
  }

  get hasUpdate$(): Observable<boolean> {
    return this.updateAvailable$.asObservable();
  }

  private initServiceWorkerUpdates(): void {
    if (!this.swUpdate.isEnabled) {
      console.warn('⚠️ Service Worker is NOT enabled');
      return;
    }

    console.log(
      '✅ Service Worker is enabled - PWA Update Service initialized',
    );

    // Listen for ALL version update events with detailed logging
    this.swUpdate.versionUpdates.subscribe((event) => {
      console.log('📦 Version Update Event:', event.type, event);

      switch (event.type) {
        case 'VERSION_DETECTED':
          console.log('🔍 New version detected on server');
          break;
        case 'VERSION_READY':
          console.log('🎉 New version ready to activate!');
          console.log('Current version:', event.currentVersion);
          console.log('Latest version:', event.latestVersion);
          this.updateAvailable$.next(true);
          break;
        case 'VERSION_INSTALLATION_FAILED':
          console.error('❌ Version installation failed:', event);
          break;
        case 'NO_NEW_VERSION_DETECTED':
          console.log('✓ No new version detected - already on latest');
          break;
      }
    });

    // Check for updates when app becomes stable
    const appIsStable$ = this.appRef.isStable.pipe(
      first((isStable) => isStable === true),
    );
    const everyHour$ = interval(60 * 60 * 1000); // Check every hour
    const everyHourOnceAppIsStable$ = concat(appIsStable$, everyHour$);

    everyHourOnceAppIsStable$.subscribe(async () => {
      console.log('🔄 Checking for updates...');
      try {
        const updateFound = await this.swUpdate.checkForUpdate();
        console.log(
          updateFound
            ? '✅ Update check: New version available!'
            : 'ℹ️ Update check: Already on latest version',
        );
      } catch (err) {
        console.error('❌ Failed to check for updates:', err);
      }
    });

    // Handle unrecoverable states
    this.swUpdate.unrecoverable.subscribe((event) => {
      console.error('💥 Unrecoverable service worker error:', event.reason);
      if (confirm('An error occurred. Reload the application?')) {
        window.location.reload();
      }
    });
  }

  public async refreshApp(): Promise<void> {
    try {
      // Save scroll position reset for after reload
      sessionStorage.setItem('pwa-update-reload', 'true');

      // Activate the new version
      await this.swUpdate.activateUpdate();

      // Scroll to top before reload to prevent Android viewport issues
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });

      // Small delay to ensure scroll completes
      setTimeout(() => {
        window.location.reload();
      }, 100);
    } catch (err) {
      console.error('Failed to activate update:', err);
      sessionStorage.removeItem('pwa-update-reload');
    }
  }

  public dismissUpdate(): void {
    this.updateAvailable$.next(false);
  }

  /**
   * Manually check for updates - useful for testing and debugging
   */
  public async manualCheckForUpdate(): Promise<boolean> {
    if (!this.swUpdate.isEnabled) {
      console.warn(
        '⚠️ Service Worker is not enabled - cannot check for updates',
      );
      return false;
    }

    console.log('🔍 Manual update check triggered...');
    try {
      const updateFound = await this.swUpdate.checkForUpdate();
      console.log(
        updateFound
          ? '✅ Manual check: New version found!'
          : 'ℹ️ Manual check: Already on latest version',
      );
      return updateFound;
    } catch (err) {
      console.error('❌ Manual update check failed:', err);
      return false;
    }
  }
}
